import React, {useEffect, useMemo, useState} from "react";
import ViewInterceptorOverlay from "./ViewInterceptorOverlay";
import {useViewInterceptor} from "../hooks/useViewInterceptor";
import {getNeuronClient} from "../utils/getNeuronClient";
import {updateNeuronDebugState} from "../utils/neuronDebugStore";

/**
 * Wrap any page (or part of your app) with this.
 * It provides:
 *  - viewInterceptor instance (rootRef, getItemRef, firedCount, etc.)
 *  - debug toggle wiring (localStorage + window event + storage)
 *
 * Usage:
 * <ViewInterceptorProvider userId={userEmail}>
 *   {(viewInterceptor) => ( ...your UI... )}
 * </ViewInterceptorProvider>
 */
const ViewInterceptorProvider = ({
  userId,
  children,
  storageKey = "neuron:viewInterceptorDebug",
  defaultEnabled = false,
}) => {
  const viewInterceptor = useViewInterceptor({userId});

  const [showDebug, setShowDebug] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored == null) return defaultEnabled;
    return stored === "true";
  });

  // Keep localStorage + other tabs in sync
  useEffect(() => {
    const handleToggle = (event) => {
      if (typeof event?.detail?.enabled !== "boolean") return;
      setShowDebug(event.detail.enabled);
    };

    const handleStorage = (event) => {
      if (event.key !== storageKey) return;
      setShowDebug(event.newValue === "true");
    };

    window.addEventListener("viewInterceptorToggle", handleToggle);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("viewInterceptorToggle", handleToggle);
      window.removeEventListener("storage", handleStorage);
    };
  }, [storageKey]);

  useEffect(() => {
    let isMounted = true;

    const syncSessionId = async () => {
      try {
        const sdk = await getNeuronClient();
        if (typeof sdk?.getSessionId !== "function") {
          return;
        }
        const sessionId = sdk.getSessionId();
        if (sessionId && isMounted) {
          updateNeuronDebugState({sessionId});
        }
      } catch (error) {
        // ignore debug sync errors
      }
    };

    void syncSessionId();

    return () => {
      isMounted = false;
    };
  }, []);

  // Keep overlay props stable-ish
  const overlayProps = useMemo(
    () => ({
      firedCount: viewInterceptor.firedCount,
      showDebug,
    }),
    [viewInterceptor.firedCount, showDebug]
  );

  return (
    <ViewInterceptorOverlay
      ref={viewInterceptor.rootRef}
      firedCount={overlayProps.firedCount}
      showDebug={overlayProps.showDebug}
    >
      {typeof children === "function" ? children(viewInterceptor) : children}
    </ViewInterceptorOverlay>
  );
};

export default ViewInterceptorProvider;
